/**
 * Integer-cent POS totals mirroring App\Support\MoneyMath + SaleFinancialCalculator.
 *
 * Money is computed in cents; percentages as basis points (18% → 1800).
 * Display / payload floats come from fromCents so they match PHP round-half-up.
 */

export const normalizeNonNegativeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(number, 0) : 0;
};

/** Round half-up to nearest integer (matches MoneyMath::roundHalfUp). */
export const roundHalfUp = (value) => Math.floor(Number(value) + 0.5);

/**
 * Scale by 100 and round half-up (mirrors MoneyMath::scaleHalfUp).
 *
 * The nudge to 4dp first is what toQuantityUnits already does: 1.005 is held
 * as 1.00499999999999989, so 1.005 * 100 lands just under the half and a bare
 * half-up rounds it down while 10.005 rounds up. Money must not disagree with
 * quantity — or with the server — about which way a half goes.
 */
const scaleHalfUp = (value) => roundHalfUp(Math.round(Number(value) * 100 * 10000) / 10000);

/**
 * Major-unit amount → integer cents.
 * Bare integers mean whole rupees (`100` → 10000), matching MoneyMath::toCents.
 */
export const toCents = (value) => {
    if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
        return value * 100;
    }
    return scaleHalfUp(normalizeNonNegativeNumber(value));
};

export const fromCents = (cents) => Math.round(Number(cents) || 0) / 100;

export const toBasisPoints = (percent) => {
    const number = Math.min(Math.max(normalizeNonNegativeNumber(percent), 0), 100);
    return scaleHalfUp(number);
};

export const percentOf = (amountCents, basisPoints) => {
    const amount = Math.max(0, Math.trunc(amountCents));
    const bps = Math.max(0, Math.trunc(basisPoints));
    return roundHalfUp((amount * bps) / 10000);
};

export const embeddedTaxCents = (lineTotalCents, taxBps) => {
    const total = Math.max(0, Math.trunc(lineTotalCents));
    const bps = Math.max(0, Math.trunc(taxBps));
    if (total === 0 || bps === 0) return 0;
    return roundHalfUp((total * bps) / (10000 + bps));
};

/**
 * The value hiding inside a tax-inclusive amount (mirrors
 * MoneyMath::exclusiveOfTaxCents).
 *
 * Subtracted rather than divided so value + embeddedTaxCents() === total
 * exactly. Used wherever a tax-inclusive figure enters the ex-tax line chain:
 * a third-schedule printed retail price, and the shelf price the POS turns into
 * the rate it quotes.
 */
export const exclusiveOfTaxCents = (inclusiveCents, taxBps) => {
    const total = Math.max(0, Math.trunc(inclusiveCents));
    return total - embeddedTaxCents(total, taxBps);
};

/**
 * A tax-inclusive shelf price → the ex-tax rate the cart carries.
 *
 * The catalogue prices goods tax-inclusive (`branch_variants.sales_price`), but
 * every figure in the cart from the rate down is a §2(46) value, so the
 * conversion happens once, when the line is added. Returns major units because
 * that is what `item.rate` holds.
 */
export const exclusiveRateFrom = (inclusiveRate, taxPercentage) =>
    fromCents(exclusiveOfTaxCents(toCents(inclusiveRate), toBasisPoints(taxPercentage)));

/** Hundredths, matching App\Support\QuantityMath::SCALE and decimal(12,2). */
export const QUANTITY_SCALE = 100;

/**
 * Major-unit quantity → integer hundredths (mirrors QuantityMath::toUnits).
 * A cart line may legitimately be 2.5 kg; flooring it to 2 quotes a total the
 * server will not agree with, and the sale is rejected as tampered.
 */
export const toQuantityUnits = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return 0;
    // Round to 4dp first so 2.675 * 100 = 267.49999... still lands on 268,
    // exactly as the PHP side does before its half-up step.
    const rounded = Math.round(number * QUANTITY_SCALE * 10000) / 10000;
    return Math.floor(rounded + 0.5);
};

/**
 * Extended line total in cents for a quantity held in hundredths.
 * Mirrors SaleFinancialCalculator::extendedCents.
 */
export const extendedCents = (unitCents, quantityUnits) => {
    const scaled = Math.max(0, Math.trunc(unitCents)) * Math.max(0, Math.trunc(quantityUnits));
    return Math.trunc(scaled / QUANTITY_SCALE) + Math.round((scaled % QUANTITY_SCALE) / QUANTITY_SCALE);
};

/**
 * Exact largest-remainder distribution so SUM(parts) = totalCents.
 * Mirrors App\Support\Apportion::cents.
 *
 * @param {number} totalCents
 * @param {Record<string|number, number>} weights
 * @returns {Record<string|number, number>}
 */
export const apportionCents = (totalCents, weights) => {
    const total = Math.trunc(totalCents);
    if (total < 0) {
        throw new Error('totalCents must be non-negative.');
    }
    const entries = Object.entries(weights ?? {});
    if (entries.length === 0) {
        if (total === 0) return {};
        throw new Error('Cannot apportion a non-zero amount across no parts.');
    }

    let weightSum = 0;
    for (const [, weight] of entries) {
        const w = Math.trunc(weight);
        if (w < 0) throw new Error('Weights must be non-negative.');
        weightSum += w;
    }

    if (weightSum <= 0) {
        if (total === 0) {
            return Object.fromEntries(entries.map(([key]) => [key, 0]));
        }
        throw new Error('Cannot apportion across zero total weight.');
    }

    const floors = {};
    const remainders = {};
    let assigned = 0;
    for (const [key, weight] of entries) {
        const exact = (total * Math.trunc(weight)) / weightSum;
        const floor = Math.floor(exact);
        floors[key] = floor;
        remainders[key] = exact - floor;
        assigned += floor;
    }

    let leftover = total - assigned;
    if (leftover === 0) return floors;

    const order = entries.map(([key]) => key);
    order.sort((a, b) => {
        const cmp = remainders[b] - remainders[a];
        if (cmp !== 0) return cmp;
        return order.indexOf(a) - order.indexOf(b);
    });

    let i = 0;
    const n = order.length;
    while (leftover > 0 && n > 0) {
        floors[order[i % n]]++;
        leftover--;
        i++;
    }

    return floors;
};

/** @deprecated Prefer fromCents(toCents(value)); kept for display-only callers. */
export const roundMoney = (value) => fromCents(toCents(value));

/**
 * Canonical POS totals used by both the visible summary and submitted payload.
 * Stages use integer cents so UI and CreateSaleJob agree to the cent.
 *
 * `item.rate` is the unit rate **excluding sales tax**, so the gross and both
 * discounts below are §2(46) figures and the tax is added to what they leave.
 * Line discounts (per item) are applied first; the header discount then applies
 * to what is left after them — matching SaleFinancialCalculator::normalize.
 */
export const lineGrossCents = (item) => {
    const units = toQuantityUnits(item?.qty);
    if (units <= 0) return 0;
    return extendedCents(toCents(item?.rate), units);
};

export const lineDiscountCents = (item) => {
    const lineGross = lineGrossCents(item);
    if (lineGross <= 0) return 0;

    const discountType = item?.discount_type === 'percent' ? 'percent' : 'rs';
    const rawDiscount = normalizeNonNegativeNumber(item?.discount);
    if (discountType === 'percent') {
        return percentOf(lineGross, toBasisPoints(Math.min(rawDiscount, 100)));
    }
    return Math.min(toCents(rawDiscount), lineGross);
};

export const lineDiscountAmount = (item) => fromCents(lineDiscountCents(item));

/**
 * The line's own value excluding sales tax, before any share of the invoice
 * discount reaches it. The figure the cart shows as Amount is this less that
 * share, plus the tax — read it off `calculateSaleSummary().lines`, which is the
 * only place the apportionment exists.
 */
export const lineNetAmount = (item) => fromCents(Math.max(lineGrossCents(item) - lineDiscountCents(item), 0));

/**
 * Display tax % for a cart line. Prefer the stored category/product rate; if
 * missing (older cart rows), recover it from the ex-tax rate and the per-unit
 * tax that was cached alongside it.
 */
export const lineTaxPercentage = (item) => {
    const stored = normalizeNonNegativeNumber(item?.tax_percentage);
    if (stored > 0) return stored;

    const exclusiveCents = toCents(item?.rate);
    const taxPerUnitCents = toCents(item?.tax_rate);
    if (exclusiveCents <= 0 || taxPerUnitCents <= 0) return 0;

    return roundHalfUp((taxPerUnitCents * 10000) / exclusiveCents) / 100;
};

export const calculateSaleSummary = (items = [], form = {}) => {
    let grossCents = 0;
    let rowDiscountCents = 0;
    const lines = [];
    items.forEach((item, itemIndex) => {
        // Mirror the server: SaleFinancialCalculator drops a line only when it
        // has no quantity, which it rejects outright. A zero-price line is
        // still a supply — a third-schedule freebie is taxed on its printed
        // retail price — so skipping it on gross alone quoted a total without
        // that tax and the checkout was rejected as tampered.
        const quantityUnits = toQuantityUnits(item?.qty);
        if (quantityUnits <= 0) return;
        const lineGross = lineGrossCents(item);
        const lineRowDiscount = lineDiscountCents(item);
        grossCents += lineGross;
        rowDiscountCents += lineRowDiscount;
        lines.push({
            // Skipping zero-quantity lines makes this array shorter than the
            // cart, so the cart index travels with the line. Without it the POS
            // read line N's tax onto cart row N and shifted every row below an
            // empty one.
            itemIndex,
            quantityUnits,
            grossCents: lineGross,
            rowDiscountCents: lineRowDiscount,
            taxBps: toBasisPoints(lineTaxPercentage(item)),
            isThirdSchedule: Boolean(item?.is_third_schedule ?? item?.third_schedule),
            // Which header taxes the line can bear. Display-side only: the
            // server re-resolves both from the catalogue and ignores the
            // payload, so a tampered cart changes the quote and nothing else.
            supplyType: ['exempt', 'zero_rated'].includes(item?.supply_type) ? item.supply_type : 'standard',
            fedExcisable: Boolean(item?.fed_excisable),
            mrpCents: toCents(item?.mrp ?? 0),
        });
    });

    const discountType = form.discount_type === 'percent' ? 'percent' : 'rs';
    const rawDiscount = normalizeNonNegativeNumber(form.discount);
    // Both header discount types work off what is left after the line discounts,
    // so the same 10% means the same thing whichever box the cashier used.
    const discountableCents = Math.max(grossCents - rowDiscountCents, 0);
    const discountValue = discountType === 'percent' ? Math.min(rawDiscount, 100) : Math.min(rawDiscount, fromCents(discountableCents));

    const headerDiscountCents =
        discountType === 'percent' ? percentOf(discountableCents, toBasisPoints(discountValue)) : Math.min(toCents(discountValue), discountableCents);

    const discountCents = Math.min(rowDiscountCents + headerDiscountCents, grossCents);

    // Gross less both discounts, still excluding sales tax. The tax-inclusive
    // net the customer settles is this plus the per-line tax accumulated below,
    // so the two are deliberately different figures.
    const valueExCents = Math.max(grossCents - discountCents, 0);

    // Push the header discount down onto the lines, largest-remainder, so the
    // per-line values of supply sum exactly to the sale's taxable amount.
    const headerShareCents = Math.max(discountCents - rowDiscountCents, 0);
    const discountWeights = {};
    lines.forEach((line, index) => {
        discountWeights[index] = Math.max(line.grossCents - line.rowDiscountCents, 0);
    });
    const headerDiscountByLine = lines.length ? apportionCents(headerShareCents, discountWeights) : {};

    let embeddedTaxCentsTotal = 0;
    let taxBasisCentsTotal = 0;
    // Value of supply plus the tax added to it, a line at a time. No longer
    // gross - discount, because gross and both discounts are ex-tax now.
    let taxableCents = 0;
    // One base per header component: each reaches a different set of lines, so
    // a single blended figure charged further tax on exempt goods and FED on
    // goods that are not excisable at all. Mirrors SaleFinancialCalculator.
    let furtherTaxBaseCents = 0;
    let fedTaxBaseCents = 0;
    let extraTaxBaseCents = 0;
    const lineBreakdown = lines.map((line, index) => {
        const apportionedDiscount = headerDiscountByLine[index] ?? 0;
        const lineDiscount = line.rowDiscountCents + apportionedDiscount;
        // Value sales excluding sales tax for this line.
        const lineValueEx = Math.max(line.grossCents - lineDiscount, 0);
        // Third-schedule goods are taxed on printed retail price, so the basis
        // ignores the discount. No MRP falls back to the line's own value. MRP
        // is a retail price and therefore tax-inclusive, so its own tax comes
        // out before it can stand as the §2(46) basis.
        const taxBasis =
            line.isThirdSchedule && line.mrpCents > 0
                ? exclusiveOfTaxCents(extendedCents(line.mrpCents, line.quantityUnits), line.taxBps)
                : lineValueEx;
        // Added on top, not carved out — the rate on screen is the rate the tax
        // stands on.
        const lineTax = percentOf(taxBasis, line.taxBps);
        const lineTotal = lineValueEx + lineTax;
        embeddedTaxCentsTotal += lineTax;
        taxBasisCentsTotal += taxBasis;
        taxableCents += lineTotal;

        const lineValueOfSupply = taxBasis;
        // Further tax (s.3(1A)) and extra tax (s.3(5)) are charges inside the
        // sales tax regime and reach taxable supplies only; further tax also
        // skips third-schedule goods, already taxed upstream on retail price.
        // FED reaches First Schedule goods only.
        const isTaxableSupply = line.supplyType === 'standard';
        const furtherTaxable = isTaxableSupply && !line.isThirdSchedule;
        if (furtherTaxable) furtherTaxBaseCents += lineValueOfSupply;
        if (isTaxableSupply) extraTaxBaseCents += lineValueOfSupply;
        if (line.fedExcisable) fedTaxBaseCents += lineValueOfSupply;

        return {
            // The cart row this line came from, so the POS can show each line
            // the tax that was actually charged on it.
            itemIndex: line.itemIndex,
            grossAmount: fromCents(line.grossCents),
            // Split out because the cart shows them separately: what the cashier
            // typed on the row, and the row's share of the invoice discount.
            rowDiscountAmount: fromCents(line.rowDiscountCents),
            apportionedDiscountAmount: fromCents(apportionedDiscount),
            discountAmount: fromCents(lineDiscount),
            valueExAmount: fromCents(lineValueEx),
            // What the customer pays for this line — the Amount column.
            taxableAmount: fromCents(lineTotal),
            taxBasisAmount: fromCents(taxBasis),
            embeddedTaxAmount: fromCents(lineTax),
            // §2(46) value excluding sales tax for this line.
            valueOfSupply: fromCents(lineValueOfSupply),
            supplyType: line.supplyType,
            furtherTaxable,
            extraTaxable: isTaxableSupply,
            fedExcisable: line.fedExcisable,
        };
    });

    // Sales Tax Act 1990 s.2(46) — the value of supply excludes the amount of
    // sales tax, so further tax, FED and extra tax stand on this and not on the
    // tax-inclusive figure. The line chain is ex-tax throughout, so the
    // accumulated basis already *is* that value. Mirrors SaleFinancialCalculator
    // exactly; if the two disagree by more than a cent the server rejects the
    // sale as tampered.
    const valueOfSupplyCents = taxBasisCentsTotal;

    const salesTaxPercentage = Math.min(normalizeNonNegativeNumber(form.salesTax), 100);
    const fedTaxPercentage = Math.min(normalizeNonNegativeNumber(form.fed_tax), 100);
    const extraTaxPercentage = Math.min(normalizeNonNegativeNumber(form.extra_tax), 100);

    // Each on its own base rather than on the blended value of supply above:
    // an exempt line still has a value, it just cannot bear these charges.
    // `salesTax` is the further-tax rate (historic field name, matching
    // sales.sales_tax_percentage).
    const taxCents = percentOf(furtherTaxBaseCents, toBasisPoints(salesTaxPercentage));
    const fedTaxCents = percentOf(fedTaxBaseCents, toBasisPoints(fedTaxPercentage));
    const extraTaxCents = percentOf(extraTaxBaseCents, toBasisPoints(extraTaxPercentage));
    const deliveryCents = toCents(form.delivery_charges ?? 0);

    // Advance income tax (236G/236H) is collected on the gross amount of the
    // sale — the whole invoice value including sales tax and delivery. It is
    // withheld for the buyer, so it is neither revenue nor output tax.
    const invoiceValueCents = taxableCents + taxCents + fedTaxCents + extraTaxCents + deliveryCents;
    const advanceTaxRate = Math.min(normalizeNonNegativeNumber(form.advance_tax_rate), 100);
    const advanceTaxCents = percentOf(invoiceValueCents, toBasisPoints(advanceTaxRate));
    const finalTotalCents = invoiceValueCents + advanceTaxCents;

    return {
        grossAmount: fromCents(grossCents),
        rowDiscountAmount: fromCents(rowDiscountCents),
        headerDiscountAmount: fromCents(headerDiscountCents),
        discountValue,
        discountAmount: fromCents(discountCents),
        // Gross less both discounts, before sales tax is added.
        valueExAmount: fromCents(valueExCents),
        taxableAmount: fromCents(taxableCents),
        netAmount: fromCents(taxableCents),
        embeddedTaxAmount: fromCents(embeddedTaxCentsTotal),
        taxBasisAmount: fromCents(taxBasisCentsTotal),
        valueOfSupply: fromCents(valueOfSupplyCents),
        // Narrower than valueOfSupply whenever the basket holds an exempt,
        // zero-rated, third-schedule or non-excisable line. Shown on the
        // checkout summary so the base each rate stands on is on screen.
        furtherTaxBase: fromCents(furtherTaxBaseCents),
        fedTaxBase: fromCents(fedTaxBaseCents),
        extraTaxBase: fromCents(extraTaxBaseCents),
        lines: lineBreakdown,
        salesTaxPercentage,
        taxAmount: fromCents(taxCents),
        fedTaxPercentage,
        fedTaxAmount: fromCents(fedTaxCents),
        extraTaxPercentage,
        extraTaxAmount: fromCents(extraTaxCents),
        deliveryCharges: fromCents(deliveryCents),
        invoiceValue: fromCents(invoiceValueCents),
        advanceTaxSection: form.advance_tax_section ?? null,
        advanceTaxRate,
        advanceTaxBase: fromCents(invoiceValueCents),
        advanceTaxAmount: fromCents(advanceTaxCents),
        finalTotal: fromCents(finalTotalCents),
    };
};
